/**
 * ConfirmConnector - Step 4 of Add Connector Wizard
 *
 * Read-only summary of the configured connector for final review. (#262
 * redesign: flat sections matching the #259/#260 idiom -- identity chip,
 * aligned definition rows, inline per-section Edit, compact health-check
 * status row. No heavy cards or stacked alerts. The page owns the
 * "Create connector" / "Back" footer; this surface is summary-only.)
 *
 * Connection Settings are rendered via DynamicConfirmation (schema-driven),
 * which also handles credential masking.
 */
import React, { useMemo } from 'react';
import { Check, Edit, Info, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ConnectorType } from '@/types/connectors';
import type { ConnectorConfigFormData } from './ConfigureConnector';
import type { HealthCheckResult } from '../ConnectorHealthCheck';
import { PlatformLogo } from '@/components/connectors/library/PlatformLogo';
import { DynamicConfirmation } from './dynamic/DynamicConfirmation';
import { DEFAULT_SCHEDULE_TIMEZONE } from '@/constants/timezone';
import { useConnectorTypes, useConnectorTypeSchema } from '@/hooks/useConnectorTypes';

interface ConfirmConnectorProps {
  connectorType: ConnectorType;
  formData: ConnectorConfigFormData;
  healthCheckResult: HealthCheckResult | null;
  onEdit: (step: number) => void;
  /** 1-based id of the Schedule step, when the type supports scheduling (#273). */
  scheduleStepId?: number;
}

/**
 * One aligned definition row. dt/dd are placed directly into the parent
 * grid so labels share a fixed-width column and values line up vertically.
 */
const DefRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <dt className="text-sm text-muted-foreground">{label}</dt>
    <dd className="min-w-0 text-sm font-medium text-foreground [overflow-wrap:anywhere]">
      {children}
    </dd>
  </>
);

/**
 * Flat section header with an inline jump-back Edit affordance.
 */
const SectionHeader: React.FC<{ title: string; onEdit: () => void }> = ({ title, onEdit }) => (
  <div className="flex items-center justify-between">
    <h3 className="text-base font-semibold tracking-tight">{title}</h3>
    <Button variant="ghost" size="sm" onClick={onEdit}>
      <Edit className="mr-1 h-4 w-4" />
      Edit
    </Button>
  </div>
);

/**
 * Small status pill using semantic tokens (Badge has no success variant).
 */
const StatusPill: React.FC<{ on: boolean; children: React.ReactNode }> = ({ on, children }) =>
  on ? (
    <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
      <Check className="h-3 w-3" />
      {children}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {children}
    </span>
  );

/**
 * Main ConfirmConnector Component
 */
export const ConfirmConnector: React.FC<ConfirmConnectorProps> = ({
  connectorType,
  formData,
  healthCheckResult,
  onEdit,
  scheduleStepId,
}) => {
  // Schema drives the dynamic connection summary; both queries are already
  // cached by the wizard controller.
  const { data: schema } = useConnectorTypeSchema(connectorType);
  const { data: connectorTypesData } = useConnectorTypes();
  const connectorTypeInfo = useMemo(
    () =>
      connectorTypesData?.connector_types?.find((t) => t.connector_type === connectorType),
    [connectorTypesData, connectorType]
  );

  const fallbackName = connectorType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  // Scheduling jumps back to the Schedule step; fall back to Configure if the
  // type has no Schedule step (shouldn't happen when schedule_enabled is true).
  const editScheduleStep = scheduleStepId ?? 2;

  return (
    <div className="space-y-8">
      {/* Connector identity chip (matches the Configure step) */}
      <div className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5">
        <PlatformLogo connectorType={connectorType} size={36} />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {connectorTypeInfo?.display_name || fallbackName}
          </div>
          {connectorTypeInfo?.category && (
            <div className="text-xs capitalize text-muted-foreground">
              {connectorTypeInfo.category}
            </div>
          )}
        </div>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => onEdit(1)}>
          <Edit className="mr-1 h-4 w-4" />
          Change
        </Button>
      </div>

      {/* General */}
      <section className="space-y-4">
        <SectionHeader title="General" onEdit={() => onEdit(2)} />
        <dl className="grid grid-cols-[minmax(0,12rem)_1fr] gap-x-5 gap-y-2.5">
          <DefRow label="Name">{formData.name || 'Not set'}</DefRow>
          <DefRow label="Description">
            {formData.description || (
              <span className="font-normal italic text-muted-foreground">No description</span>
            )}
          </DefRow>
          <DefRow label="Enable immediately">
            <StatusPill on={!!formData.enabled}>{formData.enabled ? 'Yes' : 'No'}</StatusPill>
          </DefRow>
        </dl>
      </section>

      {/* Connection */}
      <section className="space-y-4 border-t pt-8">
        <SectionHeader title="Connection settings" onEdit={() => onEdit(2)} />
        {schema && (
          <DynamicConfirmation schema={schema} values={formData as Record<string, unknown>} />
        )}
      </section>

      {/* Schedule (only when the user enabled a cadence on the Schedule step) */}
      {formData.schedule_enabled && (
        <section className="space-y-4 border-t pt-8">
          <SectionHeader title="Schedule" onEdit={() => onEdit(editScheduleStep)} />
          <dl className="grid grid-cols-[minmax(0,12rem)_1fr] gap-x-5 gap-y-2.5">
            <DefRow label="Scheduled runs">
              <StatusPill on>Enabled</StatusPill>
            </DefRow>
            <DefRow label="Schedule expression">
              <span className="font-mono font-normal">{formData.schedule_cron || 'Not set'}</span>
            </DefRow>
            <DefRow label="Timezone">{formData.schedule_timezone || DEFAULT_SCHEDULE_TIMEZONE}</DefRow>
          </dl>
        </section>
      )}

      {/* Connection test — compact inline status row */}
      {healthCheckResult &&
        (healthCheckResult.status === 'healthy' ? (
          <div className="border-t pt-8">
            <div className="flex items-center gap-3 rounded-lg border border-success/35 bg-success/[0.08] px-4 py-3">
              <Check className="h-5 w-5 flex-shrink-0 text-success" />
              <div>
                <p className="text-sm font-semibold">Connection verified</p>
                <p className="text-xs text-muted-foreground">
                  The connector reached the data source successfully.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="border-t pt-8">
            <div className="flex items-center gap-3 rounded-lg border border-warning/45 bg-warning/[0.12] px-4 py-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-warning" />
              <div className="flex-1">
                <p className="text-sm font-semibold">Connection test skipped or failed</p>
                <p className="text-xs text-muted-foreground">
                  You can still create the connector, but it may not work until the configuration
                  is fixed.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => onEdit(3)}>
                Test again
              </Button>
            </div>
          </div>
        ))}

      {/* On create — quiet explanatory footnote */}
      <div className="flex gap-2.5 border-t pt-8 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          <strong className="font-semibold text-foreground">On create</strong>
          <ul className="mt-1.5 list-disc space-y-1 pl-4">
            <li>The connector is saved{formData.enabled ? ' and enabled immediately' : ''}.</li>
            {formData.schedule_enabled && (
              <li>Scheduled extractions begin on the cron expression above.</li>
            )}
            <li>
              You can run manual extractions and edit settings any time from its detail page.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
